import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ConnectionStatus } from "../components/ConnectionStatus";
import { ThemeToggle, useFocusTrap, useRail } from "../ui";
import { useSession } from "../auth/session";
import { PAGE_TITLE_ID, usePageTitleValue } from "./pageTitle";

const SIDEBAR_ID = "app-sidebar";
const NAV_ID = "app-nav";
const PROFILE_MENU_ID = "topbar-profile-menu";
const PROFILE_BUTTON_ID = "topbar-profile";

// The authenticated app CHROME (Verso, per the FeedHive base): a persistent left sidebar carrying the
// brand above the navigation, a slim top bar carrying the page heading and the global actions
// (language, theme, profile/sign-out), and the content frame between them.
//
// Everything here is the same for every SPA entry in this workspace, which is why it is its own
// component. ADR-0014 §6 phase 2 put a SECOND entry beside the product (apps/platform, the operator
// console): the two differ in which rows the navigation holds and in nothing else, so the `nav` prop is
// the whole of the difference. The alternative was a second copy of the drawer's focus trap, the Esc
// handling and the profile popup — three a11y contracts that are correct here and would have had to be
// kept correct twice.
//
// The page's <h1> lives in the top bar, on the same line as the global actions, so the content area
// starts with content. Routes feed it through the title channel in ./pageTitle — a route `handle` for
// the standing title plus `usePageTitle()` for one that is data (a course name); see that file for why
// the handle is the mechanism and what the bar reads while a route loads. A route that supplies no title
// renders no heading at all, not an empty one. The channel is OWNED by `PageTitleRoot`, above the whole
// route tree, because it writes the browser tab as well as this heading and the pre-session pages are
// outside this shell. The shell is one of its readers (usePageTitleValue), not its host.
//
// Responsive: at ≥60rem the sidebar is the persistent desktop column. Below that the column would
// otherwise wrap the role-scoped links into a queue above the content, so the sidebar collapses into an
// off-canvas drawer opened by a hamburger toggle in the top bar. The toggle owns the open state
// (aria-expanded + aria-controls); opening moves focus into the drawer and traps Tab there; Esc /
// backdrop close it and return focus to the toggle; a route change closes it. The desktop sidebar is
// untouched — CSS in shell.css only applies the drawer treatment inside the media query.
export function AppShell({
  brandName,
  nav,
  tenant,
}: {
  /** The product or console name beside the Verso mark in the sidebar head. */
  brandName: string;
  /**
   * The navigation rows, as `<div className="app-nav__section">` groups. Supplied by the entry's own
   * layout, which is where the capability gating of each row belongs — the shell decides nothing about
   * who may see what. The rail toggle is appended after them by the shell itself (it is about the
   * navigation, so it has to sit in the column it resizes).
   */
  nav: ReactNode;
  /**
   * WHICH ORGANISATION this page is — the product's tenant pill, rendered first among the topbar actions.
   * Optional, and omitted by the operator console, which spans every tenant and addresses none.
   *
   * A slot rather than something the shell decides, for the `nav` prop's reason: the shell carries the
   * chrome and knows nothing about who the reader is. The product passes
   * `auth/tenantPicker.tsx TenantIdentity`, which since the owner's decision of 3.11.2026 draws the
   * organisation's name and logo for EVERY signed-in reader, and is the tenant switch as well for an
   * account whose token carries memberships (ADR-0018 §6).
   */
  tenant?: ReactNode;
}) {
  const { t } = useTranslation();
  const { session, signOut } = useSession();
  const location = useLocation();
  const pageTitle = usePageTitleValue();

  const [menuOpen, setMenuOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // The desktop rail. The collapsed flag lives on <html data-rail> (set pre-paint by the
  // inline script in index.html) and the CSS keys off it, so React never decides the width
  // on first paint — it only keeps the attribute and localStorage in step from here on.
  const [railCollapsed, setRailCollapsed] = useRail();

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const profileToggleRef = useRef<HTMLButtonElement>(null);
  const displayName = session?.displayName?.trim() ?? "";

  // Close (optionally returning focus to the toggle — for Esc / backdrop, not a route change,
  // where focus should follow the navigation instead of yanking back to the hamburger).
  const closeMenu = useCallback((returnFocus = false) => {
    setMenuOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  // A route change (any NavLink activation) collapses the drawer; focus follows the new page.
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  // The profile menu is a plain popup, not a modal: no focus trap and no scroll lock (those would
  // be wrong for a two-item menu that Tab should simply walk out of). It only needs the two ways
  // every popup must close — a click outside, and Esc, which hands focus back to the pill so the
  // keyboard user is not dropped at the top of the document.
  useEffect(() => {
    if (!profileOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!profileRef.current?.contains(e.target as Node)) setProfileOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setProfileOpen(false);
      profileToggleRef.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [profileOpen]);

  // While open the drawer is modal in behaviour without being a dialog, so it uses the SAME focus trap as
  // every aria-modal dialog in the app (src/ui/useFocusTrap.ts — one implementation, not two): focus moves
  // to the first link, Tab cycles inside the drawer, Esc closes and hands focus back to the toggle (the
  // hook's own restore is off — closeMenu(true) does it explicitly, and a route-change close must let focus
  // follow the navigation instead). On desktop the toggle is hidden, so menuOpen stays false and none of
  // this runs.
  useFocusTrap(drawerRef, {
    active: menuOpen,
    initialFocus: "first",
    restoreFocus: false,
    onEscape: () => closeMenu(true),
  });

  // Lock body scroll while the drawer is open so the backdrop reads as modal.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  return (
    <div className="app-shell" data-menu-open={menuOpen}>
      <a className="skip-link" href="#main">
        {t("app.skipToContent")}
      </a>

      <aside
        id={SIDEBAR_ID}
        ref={drawerRef}
        className="app-sidebar"
        data-open={menuOpen}
        data-testid="app-sidebar"
      >
        <div className="app-sidebar__head">
          <div className="app-brand">
            <span className="app-brand__mark" aria-hidden="true">
              V
            </span>
            <span className="app-brand__name">{brandName}</span>
          </div>
          {/* Mobile-only close affordance inside the drawer (hidden at ≥60rem). */}
          <button
            type="button"
            className="app-drawer-close"
            onClick={() => closeMenu(true)}
            aria-label={t("nav.closeMenu")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* A single navigation landmark (aria-label "Päänavigaatio"); the entry's section headings
            inside it are plain text, not links. */}
        <nav id={NAV_ID} aria-label={t("nav.primary")} className="app-nav">
          {nav}

          {/* Rail toggle — the foot of the nav (pinned by `margin-top: auto`), not the top
              bar: it is about the navigation, and it is the one control that has to sit in
              the column it resizes. Hidden below 60rem, where the sidebar is a drawer and
              the hamburger already owns "show me less of this".

              It is a plain button, not a toggle button: `aria-expanded` over the nav it
              narrows says exactly what the press does, and the label names the action
              rather than the current state, so both states are correctly announced. The
              hamburger's aria-controls points at the <aside>; this one points at the <nav>,
              so the two never claim the same element. */}
          <button
            type="button"
            className="app-rail-toggle"
            aria-expanded={!railCollapsed}
            aria-controls={NAV_ID}
            aria-label={railCollapsed ? t("nav.expandRail") : t("nav.collapseRail")}
            title={railCollapsed ? t("nav.expandRail") : t("nav.collapseRail")}
            onClick={() => setRailCollapsed(!railCollapsed)}
            data-testid="rail-toggle"
          >
            <svg
              className="app-rail-toggle__chevrons"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M8.5 4.5 4 9l4.5 4.5" />
              <path d="M14 4.5 9.5 9l4.5 4.5" />
            </svg>
          </button>
        </nav>
      </aside>

      <div className="app-content">
        <header className="app-topbar">
          <button
            ref={toggleRef}
            type="button"
            className="app-menu-toggle"
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={menuOpen}
            aria-controls={SIDEBAR_ID}
            onClick={() => setMenuOpen((open) => !open)}
            data-testid="nav-toggle"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
              <path
                d="M3 6h16M3 11h16M3 16h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* The page heading. Rendered only when a route actually supplies a title — an <h1>
              holding nothing is a heading a screen reader still announces, emptily. */}
          {pageTitle && (
            <h1 id={PAGE_TITLE_ID} className="app-topbar__title" data-testid="page-title">
              {pageTitle}
            </h1>
          )}

          <div className="app-topbar__actions">
            {/* The tenant pill comes FIRST, before the controls about the reader and the page: it says
                which organisation everything to its left belongs to, and a label that followed the
                personal controls would read as one of them. Nothing is rendered for an entry that passes
                no slot, or until the organisation is known. */}
            {tenant}
            <LanguageSwitcher />
            <ThemeToggle />

            {/* Profile pill: the name, and a menu holding "Oma profiili" + sign-out. Sign-out is
                deliberately no longer a bare topbar button — it lives behind the chevrons so the
                affordance tells the truth about what opens. */}
            <div className="app-topbar__profile" ref={profileRef}>
              <button
                ref={profileToggleRef}
                type="button"
                className="app-profile"
                id={PROFILE_BUTTON_ID}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-controls={PROFILE_MENU_ID}
                onClick={() => setProfileOpen((open) => !open)}
                data-testid="profile-toggle"
              >
                {/* displayName comes from the OIDC claims (auth/oidc.ts), which fall back to ""
                    when a token carries no name/preferred_username/email. The pill has no avatar
                    or monogram, so an empty string would render a nameless pill with nothing in
                    it — hence the same fallback CertificatePage uses. */}
                <span className="app-profile__name">
                  {displayName || t("profile.nameFallback")}
                </span>
                <svg
                  className="app-profile__chevrons"
                  width="12"
                  height="18"
                  viewBox="0 0 12 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M2.5 7.2 6 3.7l3.5 3.5" />
                  <path d="M2.5 10.8 6 14.3l3.5-3.5" />
                </svg>
              </button>

              {profileOpen && (
                <div
                  className="app-profile-menu"
                  id={PROFILE_MENU_ID}
                  role="menu"
                  aria-labelledby={PROFILE_BUTTON_ID}
                >
                  {/* TODO: there is no profile route yet. The design draws this as a link, but an
                      <a href="#"> that goes nowhere promises a navigation it cannot make — so until
                      the page exists it is an aria-disabled button: same look (the menu styles hit
                      every child), still reachable by keyboard, and honest to a screen reader.
                      Replace with <NavLink to="/profile"> when that route lands.
                      NOTE (KM-E11 follow-up): the one profile setting that exists — the language CLMS
                      writes to this person in — is deliberately NOT waiting for this page. It IS the
                      language switcher two elements up, which writes `PUT /api/profile/language` for a
                      signed-in reader. A profile page must not grow a second control asking the same
                      question; it should show/reuse that one. */}
                  <button role="menuitem" type="button" aria-disabled="true" data-todo-route>
                    {t("profile.myProfile")}
                  </button>
                  <span className="app-profile-menu__sep" aria-hidden="true" />
                  <button role="menuitem" type="button" onClick={signOut}>
                    {t("auth.signOut")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <ConnectionStatus />

        {/* The skip link's target, and the decision behind it (12.9.2026).
            Now that the <h1> is up in the top bar, "#main" no longer contains the page's name, so
            the link had to be re-aimed at SOMETHING that says where you have landed. The two
            candidates were the heading itself and the first interactive element of the content.
            Neither is taken; the landmark stays the target and BORROWS the heading:

            - Not the <h1>: it sits in the top bar, in front of the language switcher, the theme
              toggle and the profile menu. Focusing it would drop the user before exactly the chrome
              a WCAG 2.4.1 bypass exists to skip, so the next Tab walks back into the bar.
            - Not the first control: "the first interactive element" is a different thing on every
              page (a filter, a tab, a row link) and nothing on a read-only page; a bypass whose
              landing spot is decided by whatever the page happens to draw first is not one a
              keyboard user can learn.

            So: `tabIndex={-1}` — a fragment target that is not focusable only SCROLLS in several
            browsers, leaving focus at the top of the document — plus `aria-labelledby` at the bar's
            <h1>, which makes the landmark announce "<page title>, main" the moment focus lands.
            A route that supplies no title (pageTitle null → no <h1> to point at) falls back to the
            generic label, so the link still lands focus in the content on EVERY route; it just
            cannot name a page that has not named itself. */}
        <main
          id="main"
          className="app-main"
          tabIndex={-1}
          aria-labelledby={pageTitle ? PAGE_TITLE_ID : undefined}
          aria-label={pageTitle ? undefined : t("app.mainContent")}
        >
          {/* The boundary the signed-in surface lands in (see ./signedInPages). It is reached ONCE
              per document — the first page inside the shell fetches the module, and every navigation
              after that resolves from the module cache without suspending at all, which is what keeps
              an in-app navigation a thing that needs no network beyond the page's own read.
              INSIDE <main>, so even that first wait leaves the top bar, the navigation and the focus
              target standing. `fallback={null}`: the gap is one fetch on a connection that has already
              served the document, and a spinner that appears and vanishes inside it is noise a screen
              reader has to read out. */}
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Backdrop behind the open drawer (mobile only — the toggle is hidden at ≥60rem, so
          menuOpen never becomes true there). Clicking it closes and returns focus to the toggle. */}
      {menuOpen && (
        <div
          className="app-backdrop"
          data-testid="nav-backdrop"
          onClick={() => closeMenu(true)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
